import styled from 'styled-components'
import Container from '../../../components/Container'
import AnchorButton from '../components/AnchorButtons'
import ContactPerson from '../components/ContactPerson'
import NewsArticleCard from '../components/NewsArticleCard'
import NewsArticle from '../components/NewsArticle'
import Close from '../../../icons/Close'
import Dialog from '../../../components/modal/Dialog'
import customToast from '../../carConfiguration/components/toast/customToast'
import Toast from '../../carConfiguration/components/toast/Toast'
import NotificationView from '../components/NotificationView'

import { TranslatedText } from '@staccx/i18n'
import { Heading, SmallHeading } from '../../../components/text/Headings'
import { useMemo, useState, useCallback } from 'react'
import {
  useChosenCompany,
  useChosenConsolidateInvoice,
  useFeatureToggles,
  useUser,
} from '../../../context/UserProvider'
import { useFleetSummary } from '../../../hooks/useFleet'
import { useNotifications } from '../hooks/useNotifications'
import { useQuery, useQueryClient } from 'react-query'
import { newsFromSanity } from '../../../api/api'
import { useI18nContext } from '../../../context/I18nProvider'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { CloseButton } from '../../../theme/Theme.Button'
import { fetchSalesPerson } from '../../../api/api'
import { useBudgetSelections, usePendingSignings } from '../../../context/SigningProvider'
import { LostCardWrapper } from '../components/LostCardWrapper'
import { DialogNew } from '../../cars/components/V3/_shared/DialogNew'
import { DriverForm } from '../../contacts/V3/drivers/DriversTable/DriverForm'

const Home = () => {
  const [selectedNewsArticle, selectedNewsArticleSet] = useState(null)
  const [newsPage, newsPageSet] = useState(0)
  const [showOrderCarDialog, showOrderCarDialogSet] = useState(false)
  const [openModal, setOpenModal] = useState(false)

  const navigate = useNavigate()
  const user = useUser()
  const company = useChosenCompany()
  const featureToggles = useFeatureToggles()

  const { language } = useI18nContext()
  const consolidateInvoice = useChosenConsolidateInvoice()

  const { summary: fleetSummary } = useFleetSummary(consolidateInvoice?.ConsolidateCode)
  const { pendingSignings } = usePendingSignings(user?.user.id)
  const { budgetSelections } = useBudgetSelections(user?.user.id)
  const notifications = useNotifications(pendingSignings, budgetSelections, fleetSummary)
  const queryClient = useQueryClient()

  const { data: news } = useQuery(['news'], () => newsFromSanity())

  const formattedNews = useMemo(() => {
    if (!news) return []
    return news
      .map((newsItem) => ({
        heading: newsItem?.newsHeading?.[language],
        id: newsItem?._id,
        ingress: newsItem?.newsIngress?.[language],
        body: newsItem?.newsBody?.[language],
        image: newsItem.newsImage,
        author: newsItem.newsAuthor,
        updatedAt: new Date(newsItem._updatedAt),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt)
  }, [news, language])

  const { data: salesPerson } = useQuery(['salesperson', company?.id], () => fetchSalesPerson(company?.id), {
    enabled: !!company?.id,
  })

  const onEditDriverFinish = () =>
    Promise.all([queryClient.invalidateQueries(['carPark']), queryClient.invalidateQueries(['contacts'])])

  const confirmLostCardOrder = useCallback(() => {
    return customToast.success(<TranslatedText i18nKey="ORDER_COMPLETED" />)
  }, [])

  const faultOrder = useCallback(() => {
    return customToast.error(<TranslatedText i18nKey="ORDER_COMPLETED_ERROR" />)
  }, [])

  const driverAdded = useCallback(() => {
    return customToast.success(<div>Ansatt lagt til</div>)
  }, [])

  return (
    <Container>
      <Wrapper>
        {user && (
          <div>
            <Heading>
              <TranslatedText i18nKey="WELCOME" />
              {user.user.name}!
            </Heading>
            {notifications.length > 0 && (
              <SmallHeading>
                <TranslatedText i18nKey="TASKS" />
              </SmallHeading>
            )}
          </div>
        )}
        {notifications.length > 0 && <NotificationView notifications={notifications} />}
        <Grid>
          <News>
            <TranslatedText i18nKey="NEWS">
              {(value) => (
                <SmallHeading level={2} variant="large">
                  {value}
                </SmallHeading>
              )}
            </TranslatedText>
            {formattedNews &&
              formattedNews
                .slice(newsPage, newsPage + 5)
                .map((article, i) => (
                  <NewsArticleCard
                    article={article}
                    onClick={selectedNewsArticleSet}
                    key={`article-${article?.heading}-${i}`}
                  />
                ))}
            {formattedNews?.length > 0 && (
              <NewsPagination>
                {newsPage !== 0 ? (
                  <div>
                    <TranslatedText i18nKey="PREVIOUS_PAGE">
                      {(value) => (
                        <SmallHeading onClick={() => newsPageSet(newsPage - 5)}>
                          {value}
                          ...
                        </SmallHeading>
                      )}
                    </TranslatedText>
                  </div>
                ) : (
                  <div />
                )}
                {formattedNews?.[newsPage + 1] ? (
                  <div>
                    <TranslatedText i18nKey="NEXT">
                      {(value) => (
                        <SmallHeading onClick={() => newsPageSet(newsPage + 5)}>
                          {value}
                          ...
                        </SmallHeading>
                      )}
                    </TranslatedText>
                  </div>
                ) : (
                  <div />
                )}
              </NewsPagination>
            )}
            <Dialog open={!!selectedNewsArticle} onOpenChange={selectedNewsArticleSet} width={'50vw'}>
              <Content>{selectedNewsArticle && <NewsArticle article={selectedNewsArticle} />}</Content>
            </Dialog>
          </News>
          <ButtonsContainer>
            {featureToggles.customerCarConfiguration && (
              <div>
                {user?.user?.configuratorAccess ? (
                  <AnchorButton text={<TranslatedText i18nKey="ORDER_CAR" />} action={() => navigate('/car-order')} />
                ) : (
                  <Dialog
                    open={showOrderCarDialog}
                    onOpenChange={showOrderCarDialogSet}
                    trigger={
                      <AnchorButton
                        text={<TranslatedText i18nKey="ORDER_CAR" />}
                        action={() => showOrderCarDialogSet(true)}
                      />
                    }
                  >
                    <CloseButton type={'button'}>
                      <Close onClick={() => showOrderCarDialogSet(false)} />
                    </CloseButton>
                    <HelpDialogPadding>
                      <Heading>
                        <TranslatedText i18nKey="ACCESS_FOR_CAR_CONFIGURATION" />
                      </Heading>
                      <HelpLink to={'/help'}>
                        <ClickHereForHelpText>
                          <TranslatedText i18nKey="CLICK_HERE_CAR_CONFIG" />
                        </ClickHereForHelpText>
                      </HelpLink>
                    </HelpDialogPadding>
                  </Dialog>
                )}
              </div>
            )}
            {user?.user.roleType === 2 && (
              <DialogNew
                title="Legg til ansatt"
                open={openModal}
                onOpenChange={(open) => !open && setOpenModal(false)}
                trigger={<AnchorButton text="Legg til ansatt" onClick={() => setOpenModal(true)} />}
              >
                <DriverForm
                  driver={undefined}
                  companyId={company?.id}
                  onFinish={async () => {
                    setOpenModal(false)
                    await onEditDriverFinish()
                    driverAdded()
                  }}
                  onDiscontinue={() => setOpenModal(false)}
                />
              </DialogNew>
            )}
            {featureToggles.meldSkade && <AnchorButton text={<TranslatedText i18nKey="REPORT_DAMAGE" />} />}
            <LostCardWrapper onOrder={confirmLostCardOrder} onFaultOrder={faultOrder}>
              <AnchorButton text={<TranslatedText i18nKey="LOST_CARD" />} />
            </LostCardWrapper>
            {salesPerson && <ContactPerson contactPerson={salesPerson} />}
            {featureToggles.dinKontaktperson && <ContactPerson />}
          </ButtonsContainer>
        </Grid>
      </Wrapper>
      <Toast limit={1} />
    </Container>
  )
}

export default Home

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--spacing-medium);
  @media (min-width: 1200px) {
    padding: 0;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-largePlus);
  height: 100%;
  padding-top: var(--spacing-large);
  @media (min-width: 1200px) {
    grid-template-columns: 2fr 1fr;
  }
`

const News = styled.div`
  display: grid;
  gap: var(--spacing-small);
  align-self: flex-start;
`

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-small);
  white-space: nowrap;
  height: 100%;
`

const NewsPagination = styled.div`
  display: flex;
  justify-content: space-between;

  span {
    line-height: 2.5em;
    text-decoration: underline;
    color: var(--color-primary);
    cursor: pointer;
  }
`

const HelpDialogPadding = styled.div`
  padding: 0 0 10px 0;
  width: 100%;
`

const ClickHereForHelpText = styled.div`
  margin: 8px 0 0 0;
`
const HelpLink = styled(Link)`
  text-decoration: none;
`
const Content = styled.div`
  width: 100%;
  margin-top: var(--spacing-medium);
`
